import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { Role } from '../models/Role';
import { Module, PermissionModules } from '../models/Module';
import { Operation, Operations } from '../models/Operation';
import { permissionKey } from '../models/Permission';
import { PermissionsService } from '../services/permissions.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';

@Component({
  selector: 'app-role-permissions',
  templateUrl: './role-permissions.component.html',
  styleUrls: ['./role-permissions.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCard,
    MatCheckboxModule,
    MatExpansionModule,
    MatIcon,
    MatProgressSpinnerModule,
    BackButtonComponent
  ]
})
export class RolePermissionsComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  saving = new BehaviorSubject<boolean>(false);
  roleId = '';
  role: Role | undefined;
  isSuperAdmin = false;
  readonly modules: Module[] = PermissionModules;
  // The role's grants; may include modules/keys not shown on this page, which are saved back unchanged.
  moduleIds: string[] = [];
  permissions: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private permissionsService: PermissionsService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.roleId = this.route.snapshot.paramMap.get('roleId') ?? '';
    this.loadPage();
  }

  async loadPage() {
    try {
      this.loading.next(true);
      const response = await this.permissionsService.getRolePermissions(this.roleId);

      this.role = { id: response.roleId, name: response.roleName };
      this.isSuperAdmin = response.superAdmin;
      this.moduleIds = response.moduleIds;
      this.permissions = response.permissions;
    } catch (e) {
      this.role = undefined;
      this.logService.handleError(e, 'An error occurred while retrieving Permissions', 'Error while retrieving Permissions');
    } finally {
      this.loading.next(false);
    }
  }

  getModuleOperations(moduleId: string): Operation[] {
    return Operations.filter(operation => operation.moduleId === moduleId);
  }

  isModuleChecked(moduleId: string): boolean {
    return this.isSuperAdmin || this.moduleIds.includes(moduleId);
  }

  isOperationChecked(module: Module, operationId: string): boolean {
    return this.isSuperAdmin || this.permissions.includes(permissionKey(module.id, operationId));
  }

  isOperationDisabled(moduleId: string): boolean {
    return this.isSuperAdmin || !this.isModuleChecked(moduleId);
  }

  onModuleToggle(module: Module): void {
    if (this.isSuperAdmin) {
      return;
    }

    if (this.moduleIds.includes(module.id)) {
      this.moduleIds = this.moduleIds.filter(id => id !== module.id);
      // A module that is no longer granted cannot keep operation permissions.
      this.permissions = this.permissions.filter(key => key.substring(0, key.lastIndexOf('-')) !== module.id);
    } else {
      this.moduleIds = [...this.moduleIds, module.id];
    }
  }

  onOperationToggle(module: Module, operationId: string): void {
    if (this.isOperationDisabled(module.id)) {
      return;
    }

    const key = permissionKey(module.id, operationId);
    this.permissions = this.permissions.includes(key)
      ? this.permissions.filter(existing => existing !== key)
      : [...this.permissions, key];
  }

  async onSaveClick(): Promise<void> {
    if (this.isSuperAdmin || !this.role) {
      return;
    }

    try {
      this.saving.next(true);
      const saved = await this.permissionsService.saveRolePermissions(this.roleId, {
        moduleIds: this.moduleIds,
        permissions: this.permissions
      });

      this.moduleIds = saved.moduleIds;
      this.permissions = saved.permissions;
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while saving Permissions', 'Error while saving Permissions');
    } finally {
      this.saving.next(false);
    }
  }
}
